import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * ProjectRequesterPicker — choose a ticket Requester from a PROJECT's contacts
 * roster (the people managed on Project → Contacts). Used on the Create / Edit
 * ticket forms only when a Project is selected; with no project the forms fall
 * back to the existing client-scoped RequesterPicker (unchanged), so this is
 * purely additive.
 *
 * Picking a contact sets the ticket's `requester_user_id` (each contact is a
 * portal-ready user under the project's client). Managers (project.contacts.manage)
 * get an inline "+ add" — Name / Email / Phone — that creates + links the contact
 * and selects it; everyone else picks from the existing list.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/Button';
import { Input, Select } from '@/components/ui/Input';
import { ApiError, api } from '@/lib/api';
import { hasPerm, useAuthStore } from '@/store/auth';
import { t } from '@/lib/i18n';
export function ProjectRequesterPicker({ projectId, value, onChange }) {
    const qc = useQueryClient();
    const me = useAuthStore((s) => s.me);
    const canManage = hasPerm(me, 'project.contacts.manage');
    const queryKey = ['project-contacts', projectId];
    const [adding, setAdding] = useState(false);
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');
    const [err, setErr] = useState(null);
    const contacts = useQuery({
        queryKey,
        queryFn: () => api.get(`/projects/${projectId}/contacts`),
        enabled: !!projectId,
    });
    const add = useMutation({
        mutationFn: () => api.post(`/projects/${projectId}/contacts`, {
            display_name: name.trim(),
            email: email.trim(),
            phone: phone.trim() || null,
        }),
        onSuccess: (row) => {
            setErr(null);
            setAdding(false);
            setName('');
            setEmail('');
            setPhone('');
            qc.invalidateQueries({ queryKey });
            onChange(row.user_id, { display_name: row.display_name, email: row.email });
        },
        onError: (e) => setErr(e?.message ?? 'Could not add contact.'),
    });
    const rows = contacts.data ?? [];
    const canAdd = name.trim().length > 0 && email.trim().includes('@');
    return (_jsxs("div", { className: "space-y-2", children: [_jsxs("div", { className: "flex items-center gap-2", children: [_jsxs(Select, { value: value, onChange: (e) => {
                            const hit = rows.find((r) => r.user_id === e.target.value);
                            onChange(e.target.value, hit ? { display_name: hit.display_name, email: hit.email } : undefined);
                        }, className: "flex-1", "aria-label": t('Requester (from project contacts)'), children: [_jsx("option", { value: "", children: rows.length ? '— select a contact —' : 'No contacts on this project yet' }), rows.map((c) => (_jsxs("option", { value: c.user_id, children: [c.display_name, c.email ? ` · ${c.email}` : ''] }, c.user_id)))] }), canManage && !adding && (_jsxs(Button, { type: "button", size: "sm", variant: "outline", onClick: () => setAdding(true), children: [_jsx(Plus, { size: 14 }), " New"] }))] }), canManage && adding && (_jsxs("div", { className: "grid gap-2 rounded-lg border border-slate-200 p-2 dark:border-slate-800 sm:grid-cols-[1fr_1fr_1fr_auto]", children: [_jsx(Input, { value: name, onChange: (e) => setName(e.target.value), placeholder: t('Name'), maxLength: 200, "aria-label": t('New contact name') }), _jsx(Input, { value: email, onChange: (e) => setEmail(e.target.value), placeholder: t('Email'), type: "email", maxLength: 320, "aria-label": t('New contact email') }), _jsx(Input, { value: phone, onChange: (e) => setPhone(e.target.value), placeholder: t('Phone (optional)'), maxLength: 32, "aria-label": t('New contact phone') }), _jsxs("div", { className: "flex gap-1", children: [_jsx(Button, { type: "button", size: "sm", disabled: !canAdd || add.isPending, onClick: () => add.mutate(), children: add.isPending ? 'Adding…' : 'Add' }), _jsx(Button, { type: "button", size: "sm", variant: "ghost", onClick: () => { setAdding(false); setErr(null); }, children: "Cancel" })] })] })), _jsxs("p", { className: "text-[11px] text-slate-400", children: ["Requesters come from this project\u2019s ", _jsx("b", { children: "Contacts" }), ". ", canManage ? 'Add a new one with “New”.' : 'Ask a manager to add a new contact.'] }), err && _jsx("div", { className: "text-xs text-rose-600", children: err })] }));
}
